import React from "react";
import axios from "axios";

import Loading from "../../../components/common/loading";
import ModalManager from "../../../components/common/modal/ModalManager";
import { AppPageHeader } from "../../../components/common/appShell";
import NotificationManager from "../../../utils/notificationManager";
import DeviceSerialConsole from "./deviceSerialConsole";
import "./deviceOpsExperience.css";

export default class DevicePageDiagnostics extends React.Component {
  state = {
    host: "",
    loading: false,
    status: "",
    message: "",
    errMsg: "",
  };

  handlePing = () => {
    const { host, loading } = this.state;
    const { deviceOffline, offlineReason } = this.props;

    if (loading) {
      return;
    }

    if (deviceOffline) {
      NotificationManager.alert(
        offlineReason || "Device is offline and cannot run diagnostics.",
      );
      return;
    }

    if (!host.trim()) {
      this.setState({ errMsg: "Host is required" });
      return;
    }

    this.setState({ loading: true, status: "", message: "", errMsg: "" });
    const encodedSerial = encodeURIComponent(
      this.props.match.params.enclosureSerialNumber,
    );

    axios
      .post(
        `/v1/devices/${encodedSerial}/diagnostics/ping`,
        { host: host.trim() },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        },
      )
      .then((res) => {
        const payloadMessage =
          res?.data?.data?.message ||
          res?.data?.data?.result ||
          "Ping was successful.";
        this.setState({
          status: "success",
          message: payloadMessage,
        });
        NotificationManager.success("Ping succeeded");
      })
      .catch((err) => {
        const response = err?.response;
        const text =
          response?.data?.message ||
          response?.data?.error ||
          "Ping failed. Please try again.";
        this.setState({
          status: "error",
          message: text,
        });
        NotificationManager.alert(text);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  handleReboot = () => {
    const { deviceOffline, offlineReason } = this.props;
    const canReboot = Boolean(
      this.props.user?.is_enterprise_admin ||
        this.props.user?.has_admin_privileges,
    );

    if (!canReboot) {
      NotificationManager.alert(
        "Your role does not allow management controller recovery actions.",
      );
      return;
    }

    if (deviceOffline) {
      NotificationManager.alert(
        offlineReason ||
          "Device is offline and cannot accept recovery commands.",
      );
      return;
    }

    const encodedSerial = encodeURIComponent(
      this.props.match.params.enclosureSerialNumber,
    );

    ModalManager.confirm(
      "This restarts only the management controller. Connected equipment remains powered according to the current outlet states. The device may appear offline for 1-2 minutes while it reconnects.",
      "Restart management controller?",
      "Restart Management Controller",
      async (confirmed, close) => {
        if (!confirmed) {
          return;
        }

        try {
          const res = await axios.post(
            `/v1/devices/${encodedSerial}/diagnostics/reboot`,
            {},
            {
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            },
          );

          const payloadMessage =
            res?.data?.data?.message ||
            "Restart command received. The device may appear offline for 1-2 minutes while it reconnects.";
          NotificationManager.success(payloadMessage);
        } catch (err) {
          const response = err?.response;
          const text =
            response?.data?.message ||
            response?.data?.error ||
            "Failed to restart management controller. Please try again.";
          NotificationManager.alert(text);
        } finally {
          if (close) {
            close();
          }
        }
      },
    );
  };

  renderPingStatus() {
    const { status, message, loading } = this.state;
    if (loading) {
      return <Loading black />;
    }

    if (!status) {
      return null;
    }

    const heading = status === "success" ? "Success" : "Unreachable";
    return (
      <div
        className={`device-ops-status-block ${
          status === "success"
            ? "device-ops-status-block--success"
            : "device-ops-status-block--error"
        }`}
      >
        <div className="device-ops-status-title">{heading}</div>
        <div className="device-ops-status-message">{message}</div>
      </div>
    );
  }

  render() {
    const { host, errMsg, loading } = this.state;
    const { deviceOffline, offlineReason, shellState } = this.props;
    const isAdmin = Boolean(
      this.props.user?.is_enterprise_admin ||
        this.props.user?.has_admin_privileges,
    );
    const canReboot = isAdmin;
    const actionBlocked = Boolean(deviceOffline);
    const rebootDisabled = !canReboot || actionBlocked;

    return (
      <div className="device-ops-page">
        <div className="device-ops-page__intro">
          <AppPageHeader
            title="Diagnostics"
            description={
              isAdmin
                ? "Run connectivity checks and shell troubleshooting tools for this device."
                : "Run connectivity checks for this device."
            }
          />
          <p className="device-ops-header-note">
            Use these diagnostics tools to verify network reachability and inspect
            device state without changing outlet power.
          </p>
        </div>

        {actionBlocked ? (
          <div className="device-ops-callout device-ops-callout--warning">
            {offlineReason ||
              "Device is offline and cannot run diagnostics until it reconnects."}
          </div>
        ) : null}

        <div className="device-ops-grid device-ops-grid--single">
          <section className="device-ops-card">
            <div className="device-ops-card-heading">
              <h3 className="device-ops-card-title">Ping from PDU</h3>
              <p className="device-ops-card-subtitle">
                Send an ICMP ping from this device to a hostname or IP address.
              </p>
            </div>

            <div className="device-ops-field">
              <label
                className="device-ops-field-label"
                htmlFor="diagnostics-host-input"
              >
                Host Domain or IP
              </label>
              <p className="device-ops-field-hint">
                Example: 8.8.8.8 or example.com
              </p>
              <input
                id="diagnostics-host-input"
                className="device-ops-input"
                value={host}
                placeholder="8.8.8.8"
                onChange={(event) => this.setState({ host: event.target.value })}
                onBlur={() => {
                  if (host.trim()) {
                    this.setState({ errMsg: "" });
                  }
                }}
                disabled={loading || actionBlocked}
              />
            </div>

            {errMsg ? <div className="device-ops-error-text">{errMsg}</div> : null}

            <div className="device-ops-actions">
              <button
                type="button"
                className={`device-ops-button device-ops-button--primary ${
                  loading || actionBlocked ? "device-ops-button--disabled" : ""
                }`}
                onClick={loading || actionBlocked ? undefined : this.handlePing}
                disabled={loading || actionBlocked}
              >
                {loading ? <Loading black /> : null}
                Send Ping
              </button>

              <button
                type="button"
                className="device-ops-button device-ops-button--secondary"
                onClick={() =>
                  this.setState({ host: "", status: "", message: "", errMsg: "" })
                }
              >
                Reset
              </button>
            </div>

            {this.renderPingStatus()}
          </section>
        </div>

        {isAdmin ? (
          <>
            <div className="device-ops-grid device-ops-grid--single">
              <section
                id="shell-workspace"
                className="device-ops-card device-ops-anchor-card"
              >
                <DeviceSerialConsole
                  match={this.props.match}
                  user={this.props.user}
                  deviceOffline={deviceOffline}
                  offlineReason={offlineReason}
                  shellState={shellState}
                  hideOfflineCallout={actionBlocked}
                />
              </section>
            </div>

            <details className="device-ops-advanced">
              <summary className="device-ops-advanced-summary">
                <span>
                  <span className="device-ops-advanced-title">
                    Advanced recovery actions
                  </span>
                  <span className="device-ops-advanced-copy">
                    Use only when management access is degraded.
                  </span>
                </span>
              </summary>

              <section className="device-ops-card device-ops-card--recovery">
                <div className="device-ops-card-heading">
                  <h3 className="device-ops-card-title">Management Recovery</h3>
                  <p className="device-ops-card-subtitle">
                    Restart the management controller. Connected equipment remains powered on and outlet states are not changed.
                  </p>
                </div>

                <div className="device-ops-actions">
                  <button
                    type="button"
                    className={`device-ops-button device-ops-button--warning ${
                      rebootDisabled ? "device-ops-button--disabled" : ""
                    }`}
                    onClick={!rebootDisabled ? this.handleReboot : undefined}
                    disabled={rebootDisabled}
                  >
                    Restart Management Controller
                  </button>
                </div>
              </section>
            </details>
          </>
        ) : null}
      </div>
    );
  }
}
